'use client'
import { AmazonPayButtonInItemPage } from '@/app/ui/parts/default/sp/parts/buttons/item/amazonPayButton'
import { AmazonPayConfig } from '@/lib/amazonpay/server'
import { useAddCartItemOptions, useBuyItemOptions } from '@/lib/cart/logic'
import { addAdditionalPurchaseOptionToCart } from '@/lib/cart/server'
import { categoryType } from '@/lib/category'
import { colorWithOrderType } from '@/lib/color/type'
import { itemOptionType, itemType } from '@/lib/item/server'
import { serviceType } from '@/lib/service/server'
import {
  additionalPurchaseOptionElement,
  isAdditionalPurchaseOption,
} from '@/lib/service_type'
import { sizeWithOrderType } from '@/lib/size/type'
import { Fragment, useState } from 'react'
import { AddCartButton } from '../parts/buttons/addCartButton'
import { BuyButton } from '../parts/buttons/buyButton'
import { ItemOptionSelect, localItemOptionType } from './option'

const variants = ['buyAndAddCart', 'addCart'] as const
type Variant = (typeof variants)[number]

export const ItemOptionSelectForm = ({
  service,
  item,
  colors,
  sizes,
  itemOptions,
  variant = 'buyAndAddCart',
  modal = false,
  promotionModal = false,
}: {
  service: serviceType
  item: itemType
  colors: colorWithOrderType[]
  sizes: sizeWithOrderType[]
  itemOptions: itemOptionType[]
  variant?: Variant
  modal?: boolean
  promotionModal?: boolean
}) => {
  return (
    <Fragment>
      <ItemOptionSelectForm_withOptions
        service={service}
        item={item}
        itemOptions={itemOptions}
        variant={variant}
        modal={modal}
        promotionModal={promotionModal}
        colors={colors}
        sizes={sizes}
      />
    </Fragment>
  )
}

// オプションを選択して、購入もしくはカートに追加する
const ItemOptionSelectForm_withOptions = ({
  service,
  item,
  itemOptions,
  variant,
  promotionModal,
}: {
  service: serviceType
  item: itemType
  itemOptions: itemOptionType[]
  variant: Variant
  modal: boolean
  promotionModal: boolean
  colors: colorWithOrderType[]
  sizes: sizeWithOrderType[]
}) => {
  const localItemOptions = itemOptions

  const [selectedOptions, setSelectedOptions] = useState<localItemOptionType[]>(
    [localItemOptions[0]],
  )
  const [selecting, setSelecting] = useState(false)

  const disabled = selecting || selectedOptions.length === 0

  const [loadingBuy, submitBuy] = useBuyItemOptions({
    service,
    item,
  })
  const handleClickBuy = async () => {
    if (disabled) return
    await submitBuy({ itemOptions: selectedOptions })
  }

  const [loadingAddCart, submitAddCart] = useAddCartItemOptions({
    service,
    item,
  })
  const handleClickAddCart = async () => {
    if (disabled) return
    await submitAddCart({ itemOptions: selectedOptions })
  }

  return (
    <div className="w-full mx-auto">
      <ItemOptionSelect
        itemOptions={localItemOptions}
        selectedOptions={selectedOptions}
        onChanged={(options) => {
          setSelectedOptions(options)
        }}
        onSelecting={(v) => {
          setSelecting(v)
        }}
      />
      <div className="my-6">
        {variant === 'buyAndAddCart' && (
          <>
            <div className="flex w-full my-2">
              <BuyButton
                loading={loadingBuy}
                disabled={disabled}
                onClick={() => void handleClickBuy()}
                promotionModal={promotionModal}
              />
            </div>
            <div className="flex w-full text-center">
              <AddCartButton
                loading={loadingAddCart}
                disabled={disabled}
                onClick={() => void handleClickAddCart()}
                promotionModal={promotionModal}
              />
            </div>
          </>
        )}
        {/* ボタンはBuyButtonだが、振る舞いはAddCart */}
        {variant === 'addCart' && (
          <>
            <div className="flex w-full mt-2">
              <BuyButton
                loading={loadingBuy}
                disabled={disabled}
                onClick={() => void handleClickBuy()}
                chevron={false}
                promotionModal={promotionModal}
              />
            </div>
            <div className="flex w-full mt-2">
              <BuyButton
                loading={loadingAddCart}
                disabled={disabled}
                onClick={() => void handleClickAddCart()}
                text="カートに追加する"
                chevron={false}
                addCart
                promotionModal={promotionModal}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export const ItemOptionsForMultiplePurchase = ({
  service,
  item,
  category,
  itemOptions,
  promotionModal,
  defaultItemCount = 1,
  amazonPayConfig,
}: {
  service: serviceType
  item: itemType
  category: categoryType
  itemOptions: itemOptionType[]
  variant: Variant
  modal: boolean
  promotionModal: boolean
  defaultItemCount?: number
  amazonPayConfig: AmazonPayConfig | null
}) => {
  const [selecting, setSelecting] = useState(false)
  const [selectedOptionsList, setSelectedOptionsList] = useState<
    localItemOptionType[][]
  >(
    defaultItemCount === 1
      ? [[itemOptions[0]] as localItemOptionType[]]
      : [
          [itemOptions[0]] as localItemOptionType[],
          [itemOptions[0]] as localItemOptionType[],
        ],
  )
  const [loadingBuy, submitBuy] = useBuyItemOptions({
    service,
    item,
  })
  const handleClickBuy = async () => {
    await submitBuy({ itemOptions: selectedOptionsList.flat() })
    if (additionalOptions.length > 0) {
      for (const option of additionalOptions) {
        await addAdditionalPurchaseOptionToCart(
          service.service_id,
          option.item_id,
          option.name,
        )
      }
    }
  }

  const [loadingAddCart, submitAddCart] = useAddCartItemOptions({
    service,
    item,
  })
  const handleClickAddCart = async () => {
    await submitAddCart({ itemOptions: selectedOptionsList.flat() })
    if (additionalOptions.length > 0) {
      for (const option of additionalOptions) {
        await addAdditionalPurchaseOptionToCart(
          service.service_id,
          option.item_id,
          option.name,
        )
      }
    }
    window.location.href = '/carts'
  }
  const [additionalOptions, setAdditionalOptions] = useState<
    additionalPurchaseOptionElement[]
  >([])

  return (
    <div className="w-full mx-auto">
      <div className="my-2">
        {selectedOptionsList.map((selectedOptions, index) => (
          <div key={index} className="my-2 flex items-center justify-start">
            <ItemOptionSelect
              itemOptions={itemOptions}
              selectedOptions={selectedOptions}
              onChanged={(options) => {
                const newSelectedOptionsList = [...selectedOptionsList]
                newSelectedOptionsList[index] = options
                setSelectedOptionsList(newSelectedOptionsList)
              }}
              onSelecting={(v) => {
                setSelecting(v)
              }}
            />
          </div>
        ))}
      </div>
      <div className="mt-4 flex justify-between items-center">
        <div className="flex items-center font-semibold mr-4 justify-start">
          <button
            className="text-lg bg-gray-200 w-6 h-6 flex items-center justify-center mr-4"
            disabled={selecting}
            onClick={() => {
              setSelectedOptionsList([
                ...selectedOptionsList,
                [itemOptions[0]] as localItemOptionType[],
              ])
            }}
          >
            ＋
          </button>
          {selectedOptionsList.length > 1 && (
            <button
              className="text-lg bg-gray-200 w-6 h-6 flex items-center justify-center"
              disabled={selecting}
              onClick={() => {
                setSelectedOptionsList(selectedOptionsList.slice(0, -1))
              }}
            >
              ー
            </button>
          )}
        </div>
        <div className="text-sm text-gray-500">
          合計: {selectedOptionsList.length}点
        </div>
      </div>
      {service.service_options.map((option) => {
        if (isAdditionalPurchaseOption(option)) {
          const validAdditionalPurchaseOptions = option.settings.choices.filter(
            (choice) => {
              return (
                choice.category_id === 0 ||
                choice.category_id === category.category_id
              )
            },
          )
          if (!validAdditionalPurchaseOptions) return null
          return (
            <div className="my-4" key={option.option_id}>
              {validAdditionalPurchaseOptions.map((choice) => (
                <div
                  key={choice.item_id}
                  className="flex items-center justify-start w-full my-1"
                >
                  <input
                    type="checkbox"
                    id={`additional-option-${choice.item_id}`}
                    className="mr-2"
                    checked={additionalOptions.some(
                      (opt) => opt.item_id === choice.item_id,
                    )}
                    onChange={(e) => {
                      if (selecting) return
                      if (e.target.checked) {
                        setAdditionalOptions([...additionalOptions, choice])
                      } else {
                        setAdditionalOptions(
                          additionalOptions.filter(
                            (opt) => opt.item_id !== choice.item_id,
                          ),
                        )
                      }
                    }}
                  />
                  <label
                    htmlFor={`additional-option-${choice.item_id}`}
                    className="text-sm"
                  >
                    <a
                      href={`/item/${choice.item_id}`}
                      className="text-blue-500 underline"
                    >
                      {choice.name}
                    </a>
                    (+ {choice.price.toLocaleString()} 円)
                    {choice.description != '' && (
                      <p className="text-xs text-gray-500 mt-1 text-left">
                        {choice.description}
                      </p>
                    )}
                  </label>
                </div>
              ))}
            </div>
          )
        }
      })}
      <div className="my-6">
        <div className="flex w-full mt-2">
          <BuyButton
            loading={loadingBuy}
            disabled={selecting}
            onClick={() => void handleClickBuy()}
            chevron={false}
            promotionModal={promotionModal}
          />
        </div>
        <div className="flex w-full mt-2">
          <BuyButton
            loading={loadingAddCart}
            disabled={selecting}
            onClick={() => void handleClickAddCart()}
            text="カートに追加する"
            chevron={false}
            addCart
            promotionModal={promotionModal}
          />
        </div>
      </div>
      <div className="my-1 justify-center items-center flex">
        {amazonPayConfig && (
          <AmazonPayButtonInItemPage
            service={service}
            selectedOptions={selectedOptionsList.flat()}
            amazonPayConfig={amazonPayConfig}
            additionalOptions={additionalOptions}
          />
        )}
      </div>
    </div>
  )
}
